import { theme } from "@tm/utility/global";
import styled from "styled-components";

// Shared FormattedText baseStyle constants reused across step-card components
export const cardHeaderTitleStyle = {
  color: theme.blackText,
  fontSize: "16px",
  lineHeight: "20px",
  fontWeight: 700,
};

export const cardSectionTitleStyle = {
  ...cardHeaderTitleStyle,
  fontWeight: 900,
};

export const cardSubtitleStyle = {
  color: theme.greyText,
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: 400,
};

export const cardBodyLabelStyle = {
  color: theme.blackText,
  fontSize: "14px",
  lineHeight: "18px",
  fontWeight: 400,
};

export const cardBodyValueStyle = {
  ...cardBodyLabelStyle,
  textAlign: "right" as const,
  whiteSpace: "nowrap" as const,
};

export const cardGreyLabelStyle = {
  color: theme.greyText,
  fontSize: "14px",
  lineHeight: "18px",
  fontWeight: 400,
};

export const cardCaptionStyle = {
  color: theme.greyText,
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: 400,
};

export const StepCard = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  overflow: hidden;
`;

export const StepCardDivider = styled.div`
  height: 1px;
  background: ${theme.lightSilver};
`;

/* Refund breakup */

export const PaymentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`;

export const PaymentGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 0 16px;
`;

export const BreakupRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 0 16px 0 32px;
`;

export const LabelCell = styled.div`
  flex: 1;
  min-width: 0;
`;

export const RefundRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

/* Order summary */

export const OrderSummaryHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 16px 0;
`;

export const OrderItemsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

export const OrderItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

/* Reason selector */

export const ReasonHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px 8px;
`;

export const ReasonOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 2px 16px 16px;
`;

export const ReasonOptionCard = styled.div<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  border: 1px solid
    ${(props) => (props.$selected ? theme.blueText : theme.gainsBoro)};
  border-radius: 8px;
  background: ${theme.whiteText};
  padding: 10px 12px;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
`;

export const OptionButton = styled.button`
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
  }
`;

export const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const RadioOuter = styled.div<{ $selected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid
    ${(props) => (props.$selected ? theme.blueText : theme.lightGrey)};
  background: ${theme.whiteText};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const RadioInner = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${theme.blueText};
`;

export const TextAreaWrap = styled.div<{ $hasError?: boolean }>`
  margin-top: 8px;
  min-height: 96px;
  border: 1px solid
    ${(props) => (props.$hasError ? theme.redText2 : theme.gainsBoro)};
  border-radius: 8px;
  background: ${theme.whiteText};
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 96px;
  border: 0;
  resize: vertical;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.blackText};
  background: transparent;

  &::placeholder {
    color: ${theme.lightGrey};
  }

  &:focus {
    outline: none;
  }
`;

export const ErrorText = styled.div`
  margin-top: 8px;
  color: ${theme.redText2};
  font-size: 12px;
  line-height: 16px;
`;

/* Refund mode selector */

export const RefundModeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 12px;
`;

export const RefundModes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const RefundModeCard = styled.button<{
  $selected?: boolean;
  $disabled?: boolean;
}>`
  width: 100%;
  appearance: none;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid
    ${(props) => (props.$selected ? theme.blueText : "transparent")};
  background: ${theme.gray97};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  text-align: left;
`;

export const RefundModeRow = styled.div`
  display: flex;
  align-items: center;
`;

export const RefundModeRadioWrap = styled.div`
  width: 28px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const RefundModeText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

export const RefundModeTitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ModeTag = styled.div<{ $type?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  border-radius: 4px;
  padding: 3px 6px;
  background: ${(props) =>
    props.$type === "primary" ? theme.blueText : theme.gainsBoro};
`;

/* Reason selector text styles */

export const reasonSubTitleStyle = {
  color: theme.greyText6,
  fontSize: "12px",
  lineHeight: "16px",
  fontWeight: 400,
  marginTop: "4px",
};

const optionTitleBase = {
  color: theme.blackText,
  fontSize: "14px",
  lineHeight: "18px",
  flex: 1,
};

export const optionTitleStyleDefault = { ...optionTitleBase, fontWeight: 400 };
export const optionTitleStyleSelected = { ...optionTitleBase, fontWeight: 700 };

/* Refund breakup text styles */

export const refundSummaryStyle = {
  color: theme.blackText,
  fontSize: "14px",
  lineHeight: "14px",
  fontWeight: 700,
};

export const refundBreakupLabelStyle = {
  color: theme.greyText,
  fontSize: "12px",
  lineHeight: "12px",
  fontWeight: 400,
};

export const refundBreakupValueStyle = {
  ...refundBreakupLabelStyle,
  textAlign: "right" as const,
  whiteSpace: "nowrap" as const,
};

export const refundAmountStyle = {
  color: theme.greenText5,
  fontSize: "16px",
  lineHeight: "16px",
  fontWeight: 700,
};

/* Refund mode selector text styles */

export const refundModeLinkedWithStyle = {
  color: theme.blackText,
  fontSize: "13px",
  lineHeight: "18px",
  fontWeight: 400,
  marginTop: "4px",
};

export const refundModeInfoStyle = {
  color: theme.greyText6,
  fontSize: "13px",
  lineHeight: "18px",
  fontWeight: 400,
  marginTop: "2px",
};

export const refundModeTagStyle = {
  color: theme.whiteText,
  fontSize: "10px",
  lineHeight: "12px",
  fontWeight: 700,
  textTransform: "uppercase" as const,
};

/* Support info text styles */

export const supportSubTitleStyle = {
  color: theme.greyText,
  fontSize: "13px",
  lineHeight: "18px",
  fontWeight: 400,
};

/* Support info */

export const SupportInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

export const SupportInfoHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SupportContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SupportContactLink = styled.a`
  color: ${theme.blueText};
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  text-decoration: none;
  word-break: break-word;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;
